import { isEmpty } from 'lodash';
import { useEffect, useMemo, useState } from 'react';
// eslint-disable-next-line no-restricted-imports
import { useNavigate } from 'react-router-dom-latest';
import { Button, Container, Grid, Paper, Stack, Typography } from '@common-components';
import { LocalStorage } from 'enums';
import { useLocalStorage, useSearchMarket } from 'hooks';
import { messages } from 'i18n';
import { Market } from 'types';
import Autocomplete from 'components/Autocomplete';
import { BROKER_ROUTES } from 'broker/broker-routes';

export default function SubmissionServicesPage() {
  const navigate = useNavigate();
  const { items: markets, isLoading } = useSearchMarket();

  const [selectedMarketId, setSelectedMarketId] = useLocalStorage<string | null>(
    LocalStorage.OpExSelectedMarketId,
    null,
  );
  const [selectedMarket, setSelectedMarket] = useState<Market | null>(null);

  const marketById = useMemo(() => new Map(markets.map((market) => [market.id, market])), [markets]);

  useEffect(() => {
    if (!selectedMarketId) {
      setSelectedMarket(null);
      return;
    }

    const market = marketById.get(selectedMarketId) ?? null;
    if (!market) {
      setSelectedMarketId(null);
      setSelectedMarket(null);
      return;
    }

    setSelectedMarket(market);
  }, [marketById, selectedMarketId, setSelectedMarketId]);

  // Single-select Autocomplete with disableClearable — value is always a Market
  const onChange = (value: Market | Market[] | null) => {
    const market = value as Market;
    setSelectedMarket(market);
    setSelectedMarketId(market.id);
  };

  const onSubmit = () => navigate(`/${BROKER_ROUTES.CARRIER_SUBMISSION_QUEUE}`);

  return (
    <Container maxWidth="xl" sx={{ height: 1, py: 3, display: 'flex', flexDirection: 'column' }}>
      <Typography variant="h4" mb={3}>
        {messages.menu.submissionServices}
      </Typography>

      <Grid container>
        <Grid item xs={6} component={Paper} p={3}>
          {isEmpty(markets) && !isLoading ? (
            <Stack gap={1}>
              <Typography variant="body1">{messages.submissionServicesPage.noEligibleCarriers}</Typography>
              <Typography variant="body2" color="text.secondary">
                {messages.submissionServicesPage.noEligibleCarriersHint}
              </Typography>
            </Stack>
          ) : null}

          <Stack gap={2} direction="row">
            <Stack flex={1}>
              <Autocomplete
                value={selectedMarket}
                onChange={onChange}
                options={markets}
                disabled={isLoading || isEmpty(markets)}
                getDisplayValues={(market) => ({
                  title: market.organizationName,
                })}
                placeholder={
                  isLoading
                    ? messages.submissionServicesPage.loadingCarriers
                    : messages.submissionServicesPage.selectCarrier
                }
                disableClearable
              />
            </Stack>
            <Button variant="contained" disabled={!selectedMarket} onClick={onSubmit}>
              {messages.submissionServicesPage.selectButton}
            </Button>
          </Stack>
        </Grid>
      </Grid>
    </Container>
  );
}
